import { Box } from "@mui/material";
import React from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import useTheme from "../../../../../common/hooks/useTheme";
import ActionButton from "../../../../../common/ui/ActionButton";
import { Theme } from "../../../../../providers/ThemeProvider";
import InfoIcon from "../../../svg/InfoIcon";
import CustomTooltip from "../../common/CustomTooltip";
import InlineBanner from "../../common/InlineBanner";
import PeriodNavigator from "../../common/PeriodNavigator";
import PlanSkeleton from "./components/shared/PlanSkeleton";
import WeekPlanView from "./components/weekly/WeekPlanView";
import MealItemDetailsPanel from "./components/details/MealItemDetailsPanel";
import IntakeSidePanel from "./components/intake/IntakeSidePanel";
import { formatWeekLabel, thisWeekMonday } from "./helpers/planDates";
import { useNutritionPlan } from "./store/useNutritionPlan";

const REFRESH_HINT =
  "Logged a meal or swapped a dish in the Precision app? Tap refresh and we'll catch up.";

/**
 * The plan page under Programming → Nutrition: title, the week navigator and
 * a "Refresh data" action (the plan is fetched once per week and cached, so
 * changes made in the Precision app appear only after a refresh), then the
 * week's days (`WeekPlanView`). Loading, "no plan for this week" and error
 * states are handled here so the view only renders data.
 */
const NutritionPlanView = () => {
  const theme: Theme = useTheme();
  const {
    weekStartDate,
    activeWeekStatus,
    goToPreviousWeek,
    goToNextWeek,
    goToThisWeek,
    reload,
  } = useNutritionPlan();

  const renderBody = () => {
    if (activeWeekStatus === "ready") return <WeekPlanView />;
    if (activeWeekStatus === "error") {
      return (
        <InlineBanner
          text="Couldn't load the nutrition plan."
          backgroundColor={theme.colorTokens.D[10]}
          iconColor={theme.colorTokens.D[70]}
          action={{ label: "Retry", onClick: reload }}
        />
      );
    }
    if (activeWeekStatus === "empty") {
      return (
        <InlineBanner
          text="No plan for this week."
          backgroundColor={theme.colorTokens.P[10]}
          iconColor={theme.colors_ds.PRIMARY_ICON_MAIN}
        />
      );
    }
    return <PlanSkeleton />;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerRow}>
        <Box sx={styles.title(theme)}>Nutrition Plan</Box>
        <PeriodNavigator
          label={formatWeekLabel(weekStartDate)}
          period="week"
          containsToday={weekStartDate === thisWeekMonday()}
          onPrevious={goToPreviousWeek}
          onNext={goToNextWeek}
          onToday={goToThisWeek}
        />
        <ActionButton
          label="Refresh data"
          icon={<RefreshIcon sx={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }} />}
          iconRight={
            <CustomTooltip message={REFRESH_HINT}>
              <InfoIcon />
            </CustomTooltip>
          }
          isDisable={activeWeekStatus === "loading"}
          onClick={reload}
        />
      </Box>
      <Box sx={styles.body}>{renderBody()}</Box>
      <MealItemDetailsPanel />
      <IntakeSidePanel />
    </Box>
  );
};

export default NutritionPlanView;

const styles = {
  // Bounded to the shell's available height (96px = breadcrumb row + content
  // padding, as HistoryScreen does) so the page itself never scrolls: the
  // week's rows scroll in their own ScrollableWrapper.
  container: {
    width: "100%",
    height: "calc(100vh - 96px)",
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    overflow: "hidden",
  },
  // Title left, the week navigator in the middle, the refresh action right.
  headerRow: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
  },
  title: (theme: Theme) => ({
    flexShrink: 0,
    whiteSpace: "nowrap" as "nowrap",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  // The week's ScrollableWrapper keeps 16px on its right (8px padding + the
  // 8px scrollbar gutter). Pulling the body out by that much puts the day
  // rows' right edge on the header's, under the Refresh button, with the
  // scrollbar in the page margin beyond.
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    marginRight: "-16px",
  },
};
